/**
 * MapsChoiceModal Styles
 *
 * Centered card modal with zoom-in animation.
 * Pattern similar to PractitionerExpandedCard.
 */

/* ═══════════════════════════════════════════════════════════
   BACKDROP
   ═══════════════════════════════════════════════════════════ */

.backdrop {
  position: fixed;
  inset: 0;
  background: rgba(5, 16, 25, 0.7);
  z-index: 1000;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-out;
}

@supports (backdrop-filter: blur(8px)) {
  .backdrop {
    background: rgba(5, 16, 25, 0);
    backdrop-filter: blur(0px);
    transition:
      opacity 0.2s ease-out,
      background 0.2s ease-out,
      backdrop-filter 0.2s ease-out,
      -webkit-backdrop-filter 0.2s ease-out;
  }
}

.backdrop.visible {
  opacity: 1;
  pointer-events: auto;
  background: rgba(5, 16, 25, 0.7);
}

@supports (backdrop-filter: blur(8px)) {
  .backdrop.visible {
    background: rgba(5, 16, 25, 0.6);
    backdrop-filter: blur(8px);
  }
}

.backdrop.closing {
  opacity: 0;
  pointer-events: none;
}

/* ═══════════════════════════════════════════════════════════
   CENTERING WRAPPER
   ═══════════════════════════════════════════════════════════ */

.wrapper {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1001;
  pointer-events: none;
}

/* ═══════════════════════════════════════════════════════════
   CARD
   ═══════════════════════════════════════════════════════════ */

.card {
  width: calc(100vw - 64px);
  max-width: 280px;
  background: linear-gradient(180deg, var(--color-bg-elevated) 0%, var(--color-cream) 100%);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  padding: var(--space-5);
  padding-top: var(--space-6);
  position: relative;

  /* Start invisible until FLIP positions it */
  opacity: 0;

  /* Transform origin at center for proper scaling */
  transform-origin: center center;

  /* GPU acceleration */
  will-change: transform, opacity;

  /* Enable pointer events on card */
  pointer-events: auto;
}

/* When animating (FLIP play phase) */
.card.animating {
  transition:
    transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1),
    opacity 0.2s ease-out;
}

/* Reduced motion preference */
@media (prefers-reduced-motion: reduce) {
  .card,
  .card.animating {
    animation: none;
    transition: none;
    opacity: 1;
    transform: none;
  }
}

/* ═══════════════════════════════════════════════════════════
   CLOSE BUTTON
   ═══════════════════════════════════════════════════════════ */

.closeBtn {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  background: var(--color-bg-secondary);
  border: none;
  border-radius: var(--radius-full);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all var(--duration-fast) var(--ease-out);
}

.closeBtn:hover {
  background: var(--color-bg-elevated);
  color: var(--color-text-primary);
}

.closeBtn:active {
  transform: scale(0.95);
}

.closeBtn:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* ═══════════════════════════════════════════════════════════
   TITLE
   ═══════════════════════════════════════════════════════════ */

.title {
  font-family: var(--font-display);
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  margin: 0 0 var(--space-4) 0;
}

/* ═══════════════════════════════════════════════════════════
   OPTIONS
   ═══════════════════════════════════════════════════════════ */

.options {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.2);
}

.option {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  background: transparent;
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all var(--duration-fast) var(--ease-out);
  width: 100%;
  text-align: left;
}

@media (hover: hover) and (pointer: fine) {
  .option:hover {
    background: var(--color-bg-secondary);
  }
}

.option:active {
  transform: scale(0.98);
}

.option:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.optionIcon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  background: var(--color-bg-primary);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.optionName {
  font-weight: 600;
  font-size: 15px;
  color: var(--color-text-primary);
}

/* Client storefront variant: keep the centered chooser interaction, reskin the
   modal and rows with the same neutral glass surfaces as the booking cards. */

.clientBackdrop.visible {
  background: rgba(var(--bg-page-rgb), 0.68);
}

.clientCard {
  width: calc(100vw - 48px);
  max-width: 320px;
  padding: 22px;
  background: var(--glass-bg-strong);
  border: 1px solid rgba(var(--accent-light-rgb), 0.18);
  border-radius: 22px;
  box-shadow: var(--glass-shadow), 0 24px 70px rgba(0, 0, 0, 0.5);
  backdrop-filter: var(--glass-blur);
}

.clientCloseBtn {
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: 1px solid var(--border-soft);
  color: var(--text-secondary);
  box-shadow: none;
}

.clientCloseBtn:focus-visible {
  outline: none;
  border-color: var(--border-soft);
  box-shadow: none;
}

.clientKicker {
  margin-bottom: 6px;
  color: var(--accent);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.clientTitle {
  margin: 0 0 18px;
  color: var(--text-primary);
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-size: 25px;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-align: left;
}

.clientOptions {
  gap: 10px;
}

.clientOption {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  min-height: 62px;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 11px;
  padding: 9px 11px;
  border: 1px solid rgba(var(--accent-light-rgb), 0.13);
  border-radius: 14px;
  background: var(--glass-bg);
  box-shadow: none;
  color: var(--text-primary);
  text-align: left;
  cursor: pointer;
  transition:
    border-color 160ms ease,
    background 160ms ease,
    transform 120ms ease;
}

.clientOption:active {
  transform: scale(0.985);
  background: var(--glass-bg-strong);
}

.clientOption:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
  .clientOption:hover {
    border-color: rgba(var(--accent-rgb), 0.42);
    background: var(--glass-bg-strong);
  }
}

.clientOptionIcon {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  background: transparent;
}

.clientOptionIcon svg {
  width: 30px;
  height: 30px;
}

.clientOptionName {
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.2;
}

.clientDivider {
  display: none;
}

.clientChevron {
  width: 18px;
  height: 18px;
  color: var(--accent);
}

@media (prefers-reduced-motion: reduce) {
  .clientOption {
    transition: none;
  }
}
